import { Criticidad, type CriticidadValor } from '../../domain/value-objects/Criticidad';
import { EstadoAlarma } from '../../domain/value-objects/EstadoAlarma';
import { traducirErrorSupabase } from '../../domain/errores';
import { aBooleano, aFecha, aNumero, aTexto } from '../mappers/conversiones';
import { supabase } from '../supabase/cliente';
import { ambitoSesion } from './AmbitoSesion';
import { leerNombres } from './consultas';

const TABLA_UMBRALES = 'smart_meter_thresholds';
const TABLA_ALARMAS = 'smart_meter_alarms';

/** Umbral definido sobre una magnitud concreta de un medidor. */
export interface UmbralEnergia {
  /** UUID. */
  id: string;
  /** UUID del medidor. */
  medidorId: string;
  medidorNombre: string;
  clienteId: number | null;
  variable: string;
  /** Nombre que el usuario dio a la variable, si lo puso. */
  variableEtiqueta: string | null;
  valorMinimo: number | null;
  valorMaximo: number | null;
  criticidad: Criticidad;
  activo: boolean;
  descripcion: string | null;
}

export interface DatosUmbralEnergia {
  meter_id: string;
  cliente_id: number;
  variable: string;
  variable_label: string | null;
  min_value: number | null;
  max_value: number | null;
  criticidad: CriticidadValor;
  activo: boolean;
  descripcion: string | null;
}

/** Alarma generada por un umbral de energía. */
export interface AlarmaEnergia {
  id: string;
  medidorId: string;
  medidorNombre: string;
  clienteId: number | null;
  variable: string;
  valorDisparo: number | null;
  /** `min` o `max`: qué límite se incumplió. */
  tipoViolacion: string | null;
  criticidad: Criticidad;
  estado: EstadoAlarma;
  mensaje: string | null;
  fechaInicio: Date | null;
  fechaResolucion: Date | null;
  valorResolucion: number | null;
  /**
   * Identificador de la alarma equivalente en el sistema central.
   * Sin él, la alarma quedó sólo en el subsistema de energía y no llegó a los
   * avisos generales: es lo que la app anterior llamaba «huérfana».
   */
  alarmaCentralId: string | null;
}

/**
 * Umbrales y alarmas del subsistema de energía.
 *
 * Es un subsistema aparte del de sensores: sus umbrales se definen sobre las
 * magnitudes que reporta el medidor, no sobre sensores, y sus alarmas viven en
 * su propia tabla. Por eso no comparte repositorio con `RepositorioUmbrales`.
 */
export class RepositorioEnergiaUmbrales {
  async listarUmbrales(clienteId?: number | null): Promise<UmbralEnergia[]> {
    let consulta = supabase.from(TABLA_UMBRALES).select('*');
    consulta = ambitoSesion.aplicar(consulta);
    if (clienteId) consulta = consulta.eq('cliente_id', clienteId);

    const { data, error } = await consulta;
    if (error) throw traducirErrorSupabase(error);

    const filas = (data ?? []) as Record<string, unknown>[];
    const nombres = await leerNombres(
      'smart_meter_config',
      filas.map((f) => aTexto(f.meter_id)).filter((id): id is string => id !== null),
      { columnasNombre: ['meter_name', 'meter_serial'] },
    );

    return filas
      .map((fila) => {
        const medidorId = aTexto(fila.meter_id) ?? '';

        return {
          id: aTexto(fila.id) ?? '',
          medidorId,
          medidorNombre: nombres.get(medidorId) ?? 'Medidor desconocido',
          clienteId: aNumero(fila.cliente_id),
          variable: aTexto(fila.variable) ?? '',
          variableEtiqueta: aTexto(fila.variable_label),
          valorMinimo: aNumero(fila.min_value),
          valorMaximo: aNumero(fila.max_value),
          criticidad: Criticidad.from(aTexto(fila.criticidad)),
          activo: aBooleano(fila.activo) ?? true,
          descripcion: aTexto(fila.descripcion),
        };
      })
      .sort((a, b) => a.medidorNombre.localeCompare(b.medidorNombre, 'es'));
  }

  async crearUmbral(datos: DatosUmbralEnergia): Promise<void> {
    const { error } = await supabase.from(TABLA_UMBRALES).insert(datos);
    if (error) throw traducirErrorSupabase(error);
  }

  async actualizarUmbral(id: string, datos: Partial<DatosUmbralEnergia>): Promise<void> {
    const { error } = await supabase.from(TABLA_UMBRALES).update(datos).eq('id', id);
    if (error) throw traducirErrorSupabase(error);
  }

  async eliminarUmbral(id: string): Promise<void> {
    const { error } = await supabase.from(TABLA_UMBRALES).delete().eq('id', id);
    if (error) throw traducirErrorSupabase(error);
  }

  async cambiarActivoUmbral(id: string, activo: boolean): Promise<void> {
    return this.actualizarUmbral(id, { activo });
  }

  async listarAlarmas(filtros: { clienteId?: number | null } = {}): Promise<AlarmaEnergia[]> {
    let consulta = supabase
      .from(TABLA_ALARMAS)
      .select('*')
      .order('timestamp_inicio', { ascending: false })
      .limit(300);

    if (filtros.clienteId) consulta = consulta.eq('cliente_id', filtros.clienteId);

    const { data, error } = await consulta;
    if (error) throw traducirErrorSupabase(error);

    const filas = (data ?? []) as Record<string, unknown>[];
    const nombres = await leerNombres(
      'smart_meter_config',
      filas.map((f) => aTexto(f.meter_id)).filter((id): id is string => id !== null),
      { columnasNombre: ['meter_name', 'meter_serial'] },
    );

    return filas.map((fila) => {
      const medidorId = aTexto(fila.meter_id) ?? '';

      return {
        id: aTexto(fila.id) ?? '',
        medidorId,
        medidorNombre: nombres.get(medidorId) ?? 'Medidor desconocido',
        clienteId: aNumero(fila.cliente_id),
        variable: aTexto(fila.variable) ?? '',
        valorDisparo: aNumero(fila.valor_trigger),
        tipoViolacion: aTexto(fila.tipo_violacion),
        criticidad: Criticidad.from(aTexto(fila.criticidad)),
        estado: EstadoAlarma.from(aTexto(fila.estado)),
        mensaje: aTexto(fila.mensaje),
        fechaInicio: aFecha(fila.timestamp_inicio),
        fechaResolucion: aFecha(fila.timestamp_resuelta),
        valorResolucion: aNumero(fila.valor_resuelta),
        alarmaCentralId: aTexto(fila.central_alarm_id),
      };
    });
  }
}

export const repositorioEnergiaUmbrales = new RepositorioEnergiaUmbrales();
